import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { statusStyle, formatPhone } from "@/lib/clients";
import ClientFormDialog from "@/components/ClientFormDialog";
import { ClientNotes } from "@/components/ClientNotes";
import { ClientFiles } from "@/components/ClientFiles";
import { ClientTasks } from "@/components/ClientTasks";
import { ClientCosts } from "@/components/ClientCosts";
import { ClientDocket } from "@/components/ClientDocket";
import { ClientMedicalProviders } from "@/components/ClientMedicalProviders";
import { ClientCaseDetails } from "@/components/ClientCaseDetails";
import { ClientDisbursement } from "@/components/ClientDisbursement";
import GenerateDocDialog from "@/components/GenerateDocDialog";
import ClosingPacketDialog from "@/components/ClosingPacketDialog";
import { EmailComposeDialog } from "@/components/EmailComposeDialog";
import { ActivityExportDialog } from "@/components/ActivityExportDialog";
import { ArrowLeft, Pencil, Mail, Phone, Building2, MapPin, Download, Send, MessagesSquare, FolderOpen, FileText, PackageCheck } from "lucide-react";
import { toast } from "sonner";

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" strokeWidth={1.8} />
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-gray-400">{label}</p>
        <p className="text-sm text-gray-800 mt-0.5">{value || "—"}</p>
      </div>
    </div>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [replyData, setReplyData] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [packetOpen, setPacketOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [spFolderUrl, setSpFolderUrl] = useState(null);

  const loadClient = useCallback(() => {
    api.get(`/clients/${id}`).then((r) => setClient(r.data)).catch(() => navigate("/clients"));
  }, [id, navigate]);

  useEffect(() => { loadClient(); }, [loadClient]);

  useEffect(() => {
    let active = true;
    api.get(`/clients/${id}/sharepoint-folder`)
      .then((r) => { if (active) setSpFolderUrl(r.data.web_url); })
      .catch(() => { if (active) setSpFolderUrl(null); });
    return () => { active = false; };
  }, [id]);

  if (!client) return <div className="p-8 text-sm text-gray-400">Loading…</div>;

  const discuss = async () => {
    try {
      const { data } = await api.post(`/conversations/client/${id}`);
      navigate(`/messages?c=${data.id}`);
    } catch { toast.error("Could not open discussion"); }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="client-detail-page">
      <button onClick={() => navigate("/clients")} data-testid="back-button"
        className="flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-gray-900 mb-5 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to clients
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-white border border-gray-200 rounded-sm shadow-sm p-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center font-extrabold text-xl shrink-0">
                {client.name?.[0]?.toUpperCase()}
              </div>
              <div className="min-w-0">
                <h1 className="font-heading text-xl font-bold tracking-tight text-gray-900 truncate" data-testid="detail-client-name">{client.name}</h1>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {client.client_id && (
                    <span data-testid="detail-client-id" className="font-mono text-xs font-bold tracking-wider text-[#002FA7] bg-[#002FA7]/10 px-2 py-0.5 rounded-sm">
                      ID {client.client_id}
                    </span>
                  )}
                  <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-sm border capitalize ${statusStyle(client.status)}`}>{client.status}</span>
                </div>
              </div>
            </div>

            <button onClick={() => setEditOpen(true)} data-testid="edit-client-detail-button"
              className="w-full mt-5 flex items-center justify-center gap-1.5 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm transition-colors">
              <Pencil className="w-4 h-4" /> Edit client
            </button>

            <button onClick={() => { setReplyData(null); setEmailOpen(true); }} data-testid="compose-email-button"
              className="w-full mt-2 flex items-center justify-center gap-1.5 bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm transition-colors">
              <Send className="w-4 h-4" /> Compose email
            </button>

            <button onClick={() => setExportOpen(true)} data-testid="export-client-button"
              className="w-full mt-2 flex items-center justify-center gap-1.5 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm transition-colors">
              <Download className="w-4 h-4" /> Export activity
            </button>

            <button onClick={() => setGenOpen(true)} data-testid="generate-doc-button"
              className="w-full mt-2 flex items-center justify-center gap-1.5 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm transition-colors">
              <FileText className="w-4 h-4" /> Generate document
            </button>

            <button onClick={() => setPacketOpen(true)} data-testid="closing-packet-button"
              className="w-full mt-2 flex items-center justify-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-sm font-semibold text-sm transition-colors">
              <PackageCheck className="w-4 h-4" /> One-click closing packet
            </button>

            <button onClick={discuss} data-testid="discuss-client-button"
              className="w-full mt-2 flex items-center justify-center gap-1.5 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm transition-colors">
              <MessagesSquare className="w-4 h-4" /> Discuss with team
            </button>

            {spFolderUrl && (
              <a href={spFolderUrl} target="_blank" rel="noopener noreferrer" data-testid="open-sharepoint-folder-button"
                className="w-full mt-2 flex items-center justify-center gap-1.5 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm transition-colors">
                <FolderOpen className="w-4 h-4 text-[#0078D4]" /> Open folder in SharePoint
              </a>
            )}

            <div className="border-t border-gray-100 mt-5 pt-4">
              <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-sm px-3 py-2" data-testid="client-comm-stat">
                <Mail className="w-4 h-4 text-emerald-600 shrink-0" />
                <p className="text-xs text-emerald-800">
                  <span className="font-bold">{client.email_count || 0}</span> email{(client.email_count || 0) === 1 ? "" : "s"} sent
                  {client.last_email_at && <span className="text-emerald-600"> · last {new Date(client.last_email_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>}
                </p>
              </div>
            </div>

            <div className="border-t border-gray-100 mt-4 pt-5 space-y-4">
              <Info icon={Mail} label="Email" value={client.email} />
              <Info icon={Phone} label="Phone" value={formatPhone(client.phone)} />
              <Info icon={Building2} label="Company" value={client.company} />
              <Info icon={MapPin} label="Address" value={client.address} />
            </div>

            {(client.tags || []).length > 0 && (
              <div className="border-t border-gray-100 mt-5 pt-5">
                <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Tags</p>
                <div className="flex flex-wrap gap-1.5">
                  {client.tags.map((t) => (
                    <span key={t} className="text-xs bg-[#002FA7]/10 text-[#002FA7] font-semibold px-2 py-0.5 rounded-sm">{t}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <ClientFiles key={`files-${reloadKey}`} clientId={id} />
          <ClientCosts key={`costs-${reloadKey}`} clientId={id} clientName={client.name} clientEmail={client.email} />
          <ClientDocket key={`docket-${reloadKey}`} clientId={id} clientName={client.name} />
          <ClientMedicalProviders key={`medical-${reloadKey}`} clientId={id} />
          <ClientTasks key={`tasks-${reloadKey}`} clientId={id} clientName={client.name} />
        </div>

        <div className="lg:col-span-2">
          <ClientCaseDetails key={`case-${reloadKey}`} clientId={id} />
          <ClientDisbursement key={`disb-${reloadKey}`} clientId={id} clientName={client.name} clientEmail={client.email} />
          <ClientNotes key={`notes-${reloadKey}`} clientId={id}
            onReply={(email) => { setReplyData(email); setEmailOpen(true); }} />
        </div>
      </div>

      <ClientFormDialog open={editOpen} onOpenChange={setEditOpen} client={client} onSaved={loadClient} />
      <EmailComposeDialog open={emailOpen} onOpenChange={setEmailOpen} client={client} replyData={replyData}
        onSent={() => setReloadKey((k) => k + 1)} />
      <ActivityExportDialog open={exportOpen} onOpenChange={setExportOpen} client={client} />
      <GenerateDocDialog open={genOpen} onOpenChange={setGenOpen} clientId={id} onGenerated={() => setReloadKey((k) => k + 1)} />
      <ClosingPacketDialog open={packetOpen} onOpenChange={setPacketOpen} clientId={id} clientName={client.name} onGenerated={() => setReloadKey((k) => k + 1)} />
    </div>
  );
}
